import { useEffect } from "react";
import TouchableButton from "../TouchableButton";
import ComponentThemeType from "../../models/ComponentTheme";
import ComponentTypeEnum from "../../models/ComponentTypeEnum";
import ButtonToggle from "../ButtonToggle";
import { globalAny, toReversed } from "../../utils/Utils";
import CreateAccountButton from "../CreateAccountButton";
import PrivacyPolicyButton from "../PrivacyPolicyButton";
import useOtherDataStore from "../../store/useOtherData.store";
import { UserOptionSettings } from "../../enums/UserOptionSettings";
import UserOptionProps from "../../models/UserOptionProps";
import { updateUserOptions } from "../../services/accountService";
import { Props } from "./model";
import {
	StyledButtonContainer,
	StyledLegalTitleStyle,
	StyledNarrowBodyContent,
	StyledNarrowButton,
	StyledNarrowContent,
	StyledNarrowCreateButton,
	StyledNarrowMainContent,
	StyledNarrowTextContent,
	StyledWideBodyContent,
	StyledWideContent,
	StyledWideCreateButton,
	StyledWideFooter,
	StyledWideTextContent,
} from "./styled";
import useToggleStore from "../../store/useToggle.store";
import useGenericContentStore from "../../store/genericContent.store";
import WideCreateAccountWrapper from "../Layout/WideCreateAccountWrapper";
import NarrowCreateAccountWrapper from "../Layout/NarrowCreateAccountWrapper";
import { TouchableButtonDataProps } from "../../models/TouchableButtonDataProps";
import { CreateAccountButtonProps } from "../../models/CreateAccountButtonProps ";

const OtherDataComponent = (props: Props) => {
	const { otherData, googleData, videoAnalyticsData, setOtherData, setGoogleData, setVideoAnalyticsData } = useOtherDataStore();
	const isToggleHideDataMgmt = useToggleStore((state: any) => state.isToggleHideDataMgmt);
	const { isNarrow } = useGenericContentStore((state) => ({ isNarrow: state.isNarrow }));

	/**
	 * Retrieves the button title from cached "Accept Other Data" information.
	 *
	 * @param name - The internal name of the option to search for.
	 * @param translatedName - The fallback translated name if the cached title is not available.
	 * @returns The cached title text if found, otherwise the provided translated name.
	 */
	const getButtonTitle = (name: any, translatedName: string) => {
		try {
			const cacheAcceptOtherData = JSON.parse(localStorage.getItem("cacheAcceptOtherDataCreate") ?? "{}");
			const [title] =
				cacheAcceptOtherData?.sections[0]?.childSections[0]?.options
					?.find((option: any) => option?.name === name)
					.optionsWordings?.filter((val: any) => val?.key === "option-title") ?? {};
			return title?.textValue;
		} catch {
			return translatedName;
		}
	};

	/**
	 * Defines the data configuration for the "Create Account" buttons related to other data options.
	 * Each object specifies the title, toggle state, event handlers, and whether the button is disabled.
	 */
	const createAccountButtonData: CreateAccountButtonProps[] = [
		{
			title: getButtonTitle("Google Analytics", globalAny.language.google_analytics),
			isToggle: true,
			toggleTitle: googleData,
			onPress: props.onPressGoogle,
			onPressToggle: (state: boolean) => setGoogleData(state),
			disabled: isToggleHideDataMgmt,
		},
		{
			title: getButtonTitle("Video Analytics", globalAny.language.video_analytics),
			isToggle: true,
			toggleTitle: videoAnalyticsData,
			onPress: props.onPressVideoAnalytics,
			onPressToggle: (state: boolean) => setVideoAnalyticsData(state),
			disabled: isToggleHideDataMgmt,
		},
	];

	/**
	 * Defines the configuration for the main touchable buttons (Back/Next or Close) displayed in the component.
	 * Each object contains the button title, type, disabled state, whether text should appear on the left, and the onPress handler.
	 */
	const touchableButtonData: TouchableButtonDataProps[] = [
		{
			title: globalAny.language.back,
			type: ComponentTypeEnum.Secondary,
			disabled: false,
			leftText: isNarrow,
			onPress: props.onPressSecondaryButton,
		},
		{
			title: props.isSettings ? globalAny.language.button_close : globalAny.language.next,
			type: ComponentTypeEnum.Primary,
			disabled: false,
			leftText: isNarrow,
			onPress: props.onPressPrimaryButton,
		},
	];
	/**
	 * Updates the user's data collection preferences for Google Analytics and Video Analytics.
	 * Constructs an array of `UserOptionProps` reflecting the current toggle states and
	 * sends it to the server via `updateUserOptions`.
	 */
	const onUserOptions = async () => {
		const other: UserOptionProps[] = [
			{
				optionId: UserOptionSettings.GoogleAnalytics,
				isEnabled: googleData,
				optionResponseId: 0,
			},
			{
				optionId: UserOptionSettings.VideoAnalytics,
				isEnabled: videoAnalyticsData,
				optionResponseId: 0,
			},
		];

		await updateUserOptions(undefined, other);
	};

	/**
	 * Handles the "Other Data" toggle state.
	 * When enabled, it sets both Google Analytics and Video Analytics to true.
	 * When disabled, it sets both to false.
	 */
	const handleOtherDataToggle = (state: boolean) => {
		setOtherData(state);
		if (state) {
			setGoogleData(true);
			setVideoAnalyticsData(true);
		} else {
			setGoogleData(false);
			setVideoAnalyticsData(false);
		}
	};

	/**
	 * Renders a CreateAccountButton component for "Other Data" options.
	 *
	 * @param title - The button title.
	 * @param isToggle - Whether the button includes a toggle switch.
	 * @param toggleTitle - The current state/value of the toggle.
	 * @param onPress - Function to call when the button is pressed.
	 * @param onPressToggle - Function to call when the toggle state changes.
	 * @param disabled - Whether the button is disabled.
	 * @returns A CreateAccountButton component configured with the provided props.
	 */
	const renderCreateAccountButton = ({ title, isToggle, toggleTitle, onPress, onPressToggle, disabled }: CreateAccountButtonProps) => (
		<CreateAccountButton
			key={`other-data-button-${title}`}
			title={title}
			isToggle={isToggle}
			toggleTitle={toggleTitle}
			onPress={onPress}
			onPressToggle={onPressToggle}
			disabled={disabled}
		/>
	);

	/**
	 * Renders a generic TouchableButton with provided props.
	 *
	 * @param title - The button text.
	 * @param type - Button type (e.g., primary, secondary).
	 * @param leftText - Whether text should appear on the left.
	 * @param disabled - Whether the button is disabled.
	 * @param onPress - Function to call when button is pressed.
	 * @returns A TouchableButton component.
	 */
	const renderTouchableButtonData = ({ title, type, leftText, disabled, onPress }: TouchableButtonDataProps) => (
		<TouchableButton
			key={`other-data-touchable-${title}`}
			componentTheme={ComponentThemeType.VinFast}
			type={type}
			onPress={onPress}
			disabled={disabled}
			leftText={leftText}
		>
			{title}
		</TouchableButton>
	);

	/**
	 * Renders the UI layout for wide screens (e.g., web view).
	 *
	 * - Displays a toggle switch for "Other Data" (Google Analytics + Video Analytics) at the top.
	 * - Shows a title and descriptive text, using default translations if `props.title` or `props.description` are not provided.
	 * - Renders individual "Create Account" buttons for each option in `createAccountButtonData`.
	 * - Displays primary touchable buttons (Back/Next or Close) in the footer using `touchableButtonData`.
	 * - Shows a privacy policy button if `props.showPrivacyLink` is true.
	 * - Disables toggles and buttons if `isToggleHideDataMgmt` is true.
	 * - Designed for wider screens with separate footer and content layout.
	 */
	const renderWideScreen = () => (
		<WideCreateAccountWrapper>
			<StyledWideContent disabled={isToggleHideDataMgmt}>
				<ButtonToggle defaultValue={otherData} onPress={(state) => handleOtherDataToggle(state)} toggleDisabled={isToggleHideDataMgmt} />
				<StyledWideTextContent>
					<StyledLegalTitleStyle isNarrow={isNarrow}>{props.title ?? globalAny.language.other_data}</StyledLegalTitleStyle>
					<StyledWideBodyContent>{props.description ?? globalAny.language.other_data_body}</StyledWideBodyContent>
					<StyledWideCreateButton>
						{createAccountButtonData.map((item) => {
							return renderCreateAccountButton(item);
						})}
					</StyledWideCreateButton>
				</StyledWideTextContent>
			</StyledWideContent>

			<StyledWideFooter>
				<StyledButtonContainer>
					{touchableButtonData.map((item) => {
						return renderTouchableButtonData(item);
					})}
				</StyledButtonContainer>
				{props.showPrivacyLink && <PrivacyPolicyButton onPress={props.onPressPrivacy} />}
			</StyledWideFooter>
		</WideCreateAccountWrapper>
	);

	/**
	 * Renders the UI layout for narrow screens (e.g., car view).
	 *
	 * - Displays primary touchable buttons (Back/Next or Close) at the top inside `StyledNarrowButton`.
	 * - Shows a privacy policy button if `props.showPrivacyLink` is true.
	 * - Contains the main content area (`StyledNarrowMainContent`) with:
	 *    - A toggle switch for "Other Data" (Google Analytics + Video Analytics).
	 *    - A title and descriptive text, falling back to default translations if not provided.
	 *    - Individual "Create Account" buttons for Google Analytics and Video Analytics options.
	 * - Uses `isToggleHideDataMgmt` to control whether toggles/buttons are disabled.
	 * - Maps over `touchableButtonData` and `createAccountButtonData` to dynamically render the buttons.
	 */
	const renderNarrowScreen = () => (
		<NarrowCreateAccountWrapper>
			<StyledNarrowButton>
				<StyledNarrowCreateButton>
					{toReversed(touchableButtonData).map((item) => {
						return renderTouchableButtonData(item);
					})}
				</StyledNarrowCreateButton>
				{props.showPrivacyLink && <PrivacyPolicyButton onPress={props.onPressPrivacy} />}
			</StyledNarrowButton>

			<StyledNarrowMainContent>
				<StyledNarrowContent disabled={isToggleHideDataMgmt}>
					<ButtonToggle defaultValue={otherData} onPress={(state) => handleOtherDataToggle(state)} toggleDisabled={isToggleHideDataMgmt} />
					<StyledNarrowTextContent>
						<StyledLegalTitleStyle isNarrow={isNarrow}>{props.title ?? globalAny.language.other_data}</StyledLegalTitleStyle>
						<StyledNarrowBodyContent>{props.description ?? globalAny.language.other_data_body}</StyledNarrowBodyContent>
						<StyledNarrowCreateButton>
							{createAccountButtonData.map((item) => {
								return renderCreateAccountButton(item);
							})}
						</StyledNarrowCreateButton>
					</StyledNarrowTextContent>
				</StyledNarrowContent>
			</StyledNarrowMainContent>
		</NarrowCreateAccountWrapper>
	);

	/**
	 * Synchronizes the "Other Data" main toggle with individual analytics toggles.
	 *
	 * - If either Google Analytics or Video Analytics is enabled, sets `otherData` to true.
	 * - If both are disabled, sets `otherData` to false.
	 * - Calls the `onSubmit` callback (if provided) with the combined state of both analytics toggles.
	 * - If this component is used in settings, updates the user options on the server
	 *   via `onUserOptions()` whenever either toggle changes.
	 */
	useEffect(() => {
		if (googleData || videoAnalyticsData) {
			setOtherData(true);
		} else {
			setOtherData(false);
		}

		if (props.onSubmit) {
			props.onSubmit(googleData && videoAnalyticsData);
		}

		if (props.isSettings) {
			onUserOptions();
		}
	}, [googleData, videoAnalyticsData]);

	/**
	 * Calls `onUserOptions` whenever `otherData` changes, but only if the component
	 * is in settings mode (`props.isSettings`). Ensures the user's preferences for
	 * "Other Data" are updated on the server when toggled.
	 */
	useEffect(() => {
		if (props.isSettings) {
			onUserOptions();
		}
	}, [otherData]);

	/**
	 * Calls the `onChangeGoogleData` callback whenever `googleData` changes.
	 * Ensures that the parent component is notified of the updated Google Analytics toggle state.
	 * Skips execution if the callback is not provided.
	 */
	useEffect(() => {
		if (!props.onChangeGoogleData) {
			return;
		}

		// @ts-ignore
		props.onChangeGoogleData(googleData);
	}, [googleData]);

	/**
	 * Calls the `onChangeVideoAnalyticsData` callback whenever `videoAnalyticsData` changes.
	 * Ensures that the parent component is notified of the updated Video Analytics toggle state.
	 * Skips execution if the callback is not provided.
	 */
	useEffect(() => {
		if (!props.onChangeVideoAnalyticsData) {
			return;
		}

		// @ts-ignore
		props.onChangeVideoAnalyticsData(videoAnalyticsData);
	}, [videoAnalyticsData]);

	return isNarrow ? renderNarrowScreen() : renderWideScreen();
};

export default OtherDataComponent;
